import get from 'lodash/get'
import snakeCase from 'lodash/snakeCase'
import { default as deserialize, formatMeta } from 'app/utils/deserialize'
import FormErrors from 'app/utils/form_errors'
import { fetch, create, update } from 'app/utils/data'

export default {
  namespaced: true,

  state: {
    item: null,
    prorationData: null
  },

  getters: {
    currentSubscription: (state) => state.item,
    prorationData: (state) => state.prorationData,
    hasActiveSubscription: (state) => state.item && 'active' === get(state.item, 'status')
  },

  mutations: {
    subscriptionFetched(state, { subscription }) {
      state.item = subscription
    },

    prorationDataFetched(state, data) {
      state.prorationData = data
    },

    resetState(state) {
      state.item = null
      state.prorationData = null
    },

    paymentMethodUpdated(state, { paymentMethod }) {
      const { item } = state

      if (item) {
        item.cardType = paymentMethod.cardType
        item.cardLast4 = paymentMethod.cardLast4
        item.cardExpYear = paymentMethod.cardExpYear
        item.cardExpMonth = paymentMethod.cardExpMonth
      }
    }
  },

  actions: {
    async fetchSubscription({ commit }) {
      try {
        const response = await fetch({ url: `/api/subscription` })

        const subscription = await deserialize(response.data)

        commit('subscriptionFetched', { subscription })

        return subscription
      } catch (error) {
        if (error.response.status === 404) {
          return
        } else {
          throw error
        }
      }
    },

    async cancelAtPeriodEnd({ commit, state }, value) {
      try {
        const response = await update({
          url: '/api/subscription',
          record: state.item,
          patch: true,
          changes: {
            cancelAtPeriodEnd: value
          }
        })

        const subscription = await deserialize(response.data)

        commit('subscriptionFetched', { subscription })

        return subscription
      } catch (error) {
        if (error.response.data.errors) {
          throw new FormErrors(error.response.data.errors)
        } else {
          throw error
        }
      }
    },

    async reactivateSubscription({ commit, dispatch, state }, meta = {}) {
      try {
        const response = await create({
          url: '/api/subscription/reactivate',
          record: state.item,
          data: {},
          meta: meta
        })

        const subscription = await deserialize(response.data)

        commit('subscriptionFetched', { subscription })

        await dispatch('sessions/fetchCurrentSession', { force: true }, { root: true })

        return subscription
      } catch (error) {
        if (error.response.data.errors) {
          throw new FormErrors(error.response.data.errors)
        } else {
          throw error
        }
      }
    },

    async addSeats({ commit, state, dispatch }, { seats }) {
      try {
        const previousTotal = state.item.total

        const response = await update({
          url: '/api/subscription/add-seats',
          patch: true,
          record: state.item,
          changes: {
            seats
          }
        })

        const subscription = await deserialize(response.data)
        const meta = formatMeta(get(response, ['data', 'meta'], {}))
        const totalDifference = subscription.total.amount - previousTotal.amount

        await dispatch('sessions/fetchCurrentSession', { force: true }, { root: true })

        const result = {
          subscription,
          addingSeats: seats
        }

        if (meta.invoicedImmediately) {
          result.charged = { ...previousTotal, amount: totalDifference }
        }

        return result
      } catch (error) {
        if (error.response.data.errors) {
          throw new FormErrors(error.response.data.errors)
        } else {
          throw error
        }
      }
    },

    async createSubscription({ commit, dispatch }, dataAndMeta) {
      try {
        const { sku, seats, email, processor, processorToken, recaptchaToken, paymentMethod, isBacker } = dataAndMeta

        const response = await create({
          url: isBacker ? '/api/subscription/backer' : '/api/subscription',
          type: 'subscription',
          data: {
            sku,
            seats,
            email
          },
          meta: {
            processor,
            processorToken,
            recaptchaToken,
            paymentMethod: snakeCase(paymentMethod)
          }
        })

        const subscription = await deserialize(response.data)

        commit('subscriptionFetched', { subscription })

        if (isBacker) {
          await dispatch('sessions/fetchCurrentSession', { force: true }, { root: true })
        }

        return subscription
      } catch (error) {
        throw new FormErrors(error.response.data.errors)
      }
    },

    async upgradeSubscription({ commit, state, dispatch }, { seats, sku, processor, processorToken, recaptchaToken }) {
      try {
        const response = await update({
          url: '/api/subscription',
          record: state.item,
          patch: true,
          changes: {
            sku,
            seats
          },
          meta: {
            processor,
            processorToken,
            recaptchaToken
          }
        })

        const subscription = await deserialize(response.data)

        await dispatch('sessions/fetchCurrentSession', { force: true }, { root: true })

        return subscription
      } catch (error) {
        if (error.response.data.errors) {
          throw new FormErrors(error.response.data.errors)
        } else {
          throw error
        }
      }
    },

    async fetchProrationData({ commit, state }, { seats, sku }) {
      try {
        const response = await create({
          url: '/api/proration-data',
          record: state.item,
          data: {
            sku,
            seats,
            subscription_id: state.item.id
          }
        })

        const prorationData = await deserialize(response.data)

        commit('prorationDataFetched', prorationData)

        return prorationData
      } catch (error) {
        if (error.response.data.errors) {
          throw new FormErrors(error.response.data.errors)
        } else {
          throw error
        }
      }
    }
  }
}
